if (!customElements.get('mio-collection-load-more')) {
  customElements.define(
    'mio-collection-load-more',
    class MioCollectionLoadMore extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;

        this.button = this.querySelector('[data-mio-load-more-button]');
        this.label = this.querySelector('[data-mio-load-more-label]');
        this.spinner = this.querySelector('.loading__spinner');
        this.status = this.querySelector('[data-mio-load-more-status]');
        this.grid = document.querySelector(`#product-grid[data-id="${this.dataset.sectionId}"]`);

        if (!this.button || !this.label || !this.status || !this.grid || !this.button.dataset.nextUrl) return;

        this.defaultLabel = this.label.textContent;
        this.loadedPageUrls = [];
        this.storageKey = this.buildStorageKey();
        this.button.addEventListener('click', () => this.loadNextPage());
        this.grid.addEventListener('click', (event) => this.rememberCollectionState(event));
        this.dataset.ready = 'true';
        this.restoreCollectionState();
      }

      buildStorageKey() {
        const collectionUrl = new URL(window.location.href);
        collectionUrl.hash = '';
        collectionUrl.searchParams.delete('page');
        collectionUrl.searchParams.delete('section_id');

        return `mio:collection-return:v1:${this.dataset.sectionId}:${collectionUrl.pathname}${collectionUrl.search}`;
      }

      rememberCollectionState(event) {
        if (
          !this.loadedPageUrls.length ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          !(event.target instanceof Element)
        ) {
          return;
        }

        const productLink = event.target.closest('a[href]');
        if (!productLink || !this.grid.contains(productLink)) return;

        const productUrl = new URL(productLink.href, window.location.origin);
        if (!productUrl.pathname.startsWith('/products/')) return;

        try {
          window.sessionStorage.setItem(
            this.storageKey,
            JSON.stringify({
              savedAt: Date.now(),
              loadedPageUrls: this.loadedPageUrls,
              scrollY: window.scrollY,
            })
          );
        } catch (error) {
          console.warn('Mio collection return state could not be saved:', error);
        }
      }

      readCollectionState() {
        try {
          const storedState = JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null');
          const isFresh = storedState?.savedAt && Date.now() - storedState.savedAt < 30 * 60 * 1000;
          const hasPages = Array.isArray(storedState?.loadedPageUrls) && storedState.loadedPageUrls.length;

          if (isFresh && hasPages) return storedState;
          window.sessionStorage.removeItem(this.storageKey);
        } catch (error) {
          console.warn('Mio collection return state could not be read:', error);
        }

        return null;
      }

      setLoading(isLoading) {
        this.button.disabled = isLoading;
        this.button.toggleAttribute('aria-busy', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
        this.label.textContent = isLoading ? this.dataset.loadingLabel : this.defaultLabel;
      }

      setError(hasError) {
        this.toggleAttribute('data-error', hasError);
        this.status.textContent = hasError ? this.dataset.errorMessage : '';
      }

      findSectionElement(root, selector) {
        return Array.from(root.querySelectorAll(selector)).find(
          (element) => element.dataset.sectionId === this.dataset.sectionId
        );
      }

      async fetchPage(pageUrl) {
        const publicUrl = new URL(pageUrl, window.location.origin);
        publicUrl.searchParams.delete('section_id');

        const requestUrl = new URL(publicUrl);
        requestUrl.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Collection page request failed with ${response.status}`);

        const parsedDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = Array.from(parsedDocument.querySelectorAll('#product-grid[data-id]')).find(
          (element) => element.dataset.id === this.dataset.sectionId
        );
        if (!nextGrid) throw new Error('The next collection grid was not found');

        const newItems = Array.from(nextGrid.children);
        if (!newItems.length) throw new Error('The next collection page was empty');

        const nextControl = this.findSectionElement(parsedDocument, 'mio-collection-load-more[data-section-id]');
        const nextUrl = nextControl?.querySelector('[data-mio-load-more-button]')?.dataset.nextUrl || '';

        return {
          loadedUrl: `${publicUrl.pathname}${publicUrl.search}`,
          newItems,
          nextUrl,
        };
      }

      appendItems(newItems) {
        const fragment = document.createDocumentFragment();
        newItems.forEach((item) => {
          item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
          item.removeAttribute('style');
          fragment.appendChild(item);
        });
        this.grid.appendChild(fragment);
      }

      updateNextPage(nextUrl, announceCompletion = true) {
        if (nextUrl) {
          this.button.dataset.nextUrl = nextUrl;
          this.setLoading(false);
          return;
        }

        this.button.dataset.nextUrl = '';
        this.button.disabled = true;
        this.button.removeAttribute('aria-busy');
        this.spinner?.classList.add('hidden');
        this.label.textContent = this.dataset.completeLabel;
        if (announceCompletion) this.status.textContent = this.dataset.completeMessage;
      }

      async restoreCollectionState() {
        const storedState = this.readCollectionState();
        if (!storedState) return;

        this.dataset.restoring = 'true';
        this.setLoading(true);

        try {
          let nextUrl = this.button.dataset.nextUrl;
          for (const loadedPageUrl of storedState.loadedPageUrls) {
            const page = await this.fetchPage(loadedPageUrl);
            this.appendItems(page.newItems);
            this.loadedPageUrls.push(page.loadedUrl);
            nextUrl = page.nextUrl;
          }

          this.updateNextPage(nextUrl, false);
          window.mioWishlist?.updateUI();
          window.mioClarity?.event('mio_collection_return_restored');

          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => window.scrollTo(0, Number(storedState.scrollY) || 0));
          });
        } catch (error) {
          console.error('Mio collection return state could not be restored:', error);
          window.sessionStorage.removeItem(this.storageKey);
          this.setLoading(false);
          this.setError(true);
        } finally {
          delete this.dataset.restoring;
        }
      }

      async loadNextPage() {
        if (this.button.disabled || !this.button.dataset.nextUrl) return;

        this.setError(false);
        this.setLoading(true);

        try {
          const page = await this.fetchPage(this.button.dataset.nextUrl);
          this.appendItems(page.newItems);
          this.loadedPageUrls.push(page.loadedUrl);

          this.status.textContent = this.dataset.loadedMessage.replace('[count]', String(page.newItems.length));
          window.mioWishlist?.updateUI();
          window.mioClarity?.event('mio_collection_load_more');
          this.updateNextPage(page.nextUrl);
        } catch (error) {
          console.error('Mio collection load more error:', error);
          this.setLoading(false);
          this.setError(true);
        }
      }
    }
  );
}
